import {
  Abgangsleitung,
  Kabelverteilschrank,
  TrafoStation,
  Muffe,
  Hausanschluss,
  Segment
} from '../types/network';

// ============================================================================
// Bus Index Based Connection Utilities (using cable_segments from_bus/to_bus)
// ============================================================================

/**
 * Find all segments of a cable that connect to a specific bus index
 */
export const findSegmentsConnectedToBus = (busIndex: number | undefined, cable: Abgangsleitung): Segment[] => {
  if (busIndex === undefined || busIndex === null) return [];
  if (!cable.segments?.length) return [];

  return cable.segments.filter(seg =>
    seg.fromBus === busIndex || seg.toBus === busIndex
  );
};

/**
 * Check if a cable is connected to a specific bus index
 */
export const isCableConnectedToBus = (busIndex: number | undefined, cable: Abgangsleitung): boolean => {
  return findSegmentsConnectedToBus(busIndex, cable).length > 0;
};

/**
 * Find all cables connected to a Muffe via busIndex
 */
export const findCablesConnectedToMuffe = (
  muffe: Muffe,
  allCables: Abgangsleitung[]
): Abgangsleitung[] => {
  if (muffe.busIndex === undefined || muffe.busIndex === null) return [];
  return allCables.filter(cable => isCableConnectedToBus(muffe.busIndex, cable));
};

/**
 * Find segments connected to a Muffe
 */
export const findSegmentsConnectedToMuffe = (
  muffe: Muffe,
  cable: Abgangsleitung
): Segment[] => {
  return findSegmentsConnectedToBus(muffe.busIndex, cable);
};

/**
 * Find all cables connected to a Kabelverteilschrank via busIndex
 */
export const findCablesConnectedToKvs = (
  kvs: Kabelverteilschrank,
  allCables: Abgangsleitung[]
): Abgangsleitung[] => {
  if (kvs.busIndex === undefined || kvs.busIndex === null) return [];
  return allCables.filter(cable => isCableConnectedToBus(kvs.busIndex, cable));
};

/**
 * Find segments connected to a Kabelverteilschrank
 */
export const findSegmentsConnectedToKvs = (
  kvs: Kabelverteilschrank,
  cable: Abgangsleitung
): Segment[] => {
  return findSegmentsConnectedToBus(kvs.busIndex, cable);
};

/**
 * Find all cables connected to a Hausanschluss via busIndex
 */
export const findCablesConnectedToHausanschluss = (
  hausanschluss: Hausanschluss,
  allCables: Abgangsleitung[]
): Abgangsleitung[] => {
  if (hausanschluss.busIndex === undefined || hausanschluss.busIndex === null) return [];
  return allCables.filter(cable => isCableConnectedToBus(hausanschluss.busIndex, cable));
};

/**
 * Find segments connected to a Hausanschluss
 */
export const findSegmentsConnectedToHausanschluss = (
  hausanschluss: Hausanschluss,
  cable: Abgangsleitung
): Segment[] => {
  return findSegmentsConnectedToBus(hausanschluss.busIndex, cable);
};

/**
 * Find all cables connected to a TrafoStation via busIndex (LV side primarily)
 */
export const findCablesConnectedToTrafo = (
  trafo: TrafoStation,
  allCables: Abgangsleitung[]
): Abgangsleitung[] => {
  // Primarily use LV bus index for cable connections (0.4kV side)
  const lvConnected = trafo.busIndexLv !== undefined && trafo.busIndexLv !== null
    ? allCables.filter(cable => isCableConnectedToBus(trafo.busIndexLv, cable))
    : [];

  // Also check HV bus index (10kV side - rare for distribution cables)
  const hvConnected = trafo.busIndexHv !== undefined && trafo.busIndexHv !== null
    ? allCables.filter(cable =>
        isCableConnectedToBus(trafo.busIndexHv, cable) &&
        !lvConnected.includes(cable)
      )
    : [];

  return [...lvConnected, ...hvConnected];
};

/**
 * Find segments connected to a TrafoStation
 */
export const findSegmentsConnectedToTrafo = (
  trafo: TrafoStation,
  cable: Abgangsleitung
): Segment[] => {
  const lvSegments = trafo.busIndexLv !== undefined && trafo.busIndexLv !== null
    ? findSegmentsConnectedToBus(trafo.busIndexLv, cable)
    : [];

  const hvSegments = trafo.busIndexHv !== undefined && trafo.busIndexHv !== null
    ? findSegmentsConnectedToBus(trafo.busIndexHv, cable)
    : [];

  // Combine and deduplicate
  const allSegments = [...lvSegments];
  for (const seg of hvSegments) {
    if (!allSegments.find(s => s.id === seg.id)) {
      allSegments.push(seg);
    }
  }

  return allSegments;
};

